import { useCallback } from 'react';
import styled from 'styled-components';

import { ActionButton } from '@formstack/phoenix/components/Button';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeaderImage,
} from '@formstack/phoenix/components/Modal';
import { DefaultHeader, LargeContent } from '@formstack/phoenix/components/Typography';
import { ActionVariants } from '@formstack/phoenix/components/actionVariants/constants';
import { Spacing } from '@formstack/phoenix/components/spacing';

import { ConnectorIds } from '@/common/constants';
import { useResource } from '@/common/resources';
import { renderError } from '@/common/utils/render-error';
import DocsUpsellSvg from '@/images/docs-upsell.svg';
import { useToastNotification } from '@/store/common/toast';
import { useStartProductTrialMutation } from '@/store/trial';

import { IBaseDialog } from '../interface';

const StyledHeaderImg = styled.img`
  margin-top: ${Spacing.Default};
`;

const UpsellSvgMap = {
  [ConnectorIds.Documents]: DocsUpsellSvg,
};

type UpsellDialogProps = IBaseDialog & {
  connectorId: ConnectorIds;
};

export const UpsellDialog = ({ connectorId, open, onClose }: UpsellDialogProps) => {
  const id = `${connectorId}-upsell-modal`;
  const {
    cancelLabel,
    header,
    description,
    upsellLabel,
    successMessage,
    errorTitle,
    errorDescription,
  } = useResource((resource) => ({
    cancelLabel: resource.cancel,
    upsellLabel: resource.startTrial,
    ...(connectorId === ConnectorIds.Documents
      ? resource.PRODUCT_UPSELL_DIALOGS[connectorId]
      : {
          header: '',
          description: '',
          errorTitle: '',
          errorDescription: '',
          successMessage: '',
        }),
  }));

  const [startTrial] = useStartProductTrialMutation();

  const { showSuccessNotification } = useToastNotification();

  const upsellAction = useCallback(async () => {
    onClose?.();

    try {
      await startTrial(connectorId).unwrap();
      showSuccessNotification(successMessage);
    } catch {
      renderError({
        description: errorTitle,
        message: errorDescription,
        status: 500,
      });
    }
  }, [
    connectorId,
    errorDescription,
    errorTitle,
    onClose,
    showSuccessNotification,
    startTrial,
    successMessage,
  ]);

  return (
    <Modal id={id} data-test={id} open={open} closeAction={onClose}>
      <ModalHeaderImage>
        <StyledHeaderImg
          src={connectorId === ConnectorIds.Documents ? UpsellSvgMap[connectorId] : undefined}
        />
      </ModalHeaderImage>
      <ModalHeader>
        <DefaultHeader>{header}</DefaultHeader>
      </ModalHeader>
      <ModalContent>
        <LargeContent>{description}</LargeContent>
      </ModalContent>
      <ModalFooter>
        <ActionButton
          dataTest={`${id}-cancel`}
          action={onClose}
          buttonText={cancelLabel}
          noBackground
          variant={ActionVariants.edit}
        />
        <ActionButton
          dataTest={`${id}-submit`}
          action={upsellAction}
          buttonText={upsellLabel}
          variant={ActionVariants.create}
        />
      </ModalFooter>
    </Modal>
  );
};
